import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';

/**
 * Composant cloche de notifications pour les utilisateurs non-admins
 * Affiche un badge avec le nombre de notifications non lues
 * Panel déroulant avec la liste des notifications
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Ferme le panel si on clique à l'extérieur
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Gère le clic sur une notification
  const handleNotificationClick = async (notificationId: string, link?: string, read?: boolean) => {
    if (!read) {
      await markAsRead(notificationId);
    }
    
    if (link) {
      navigate(link);
    }
    
    setIsOpen(false);
  };

  // Gère le marquage de toutes les notifications comme lues
  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  // Icône selon le type de notification
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'candidature_status':
        return 'ri-file-list-3-line';
      case 'ticket_reply':
        return 'ri-message-3-line';
      case 'societe_status':
        return 'ri-building-line';
      case 'system':
        return 'ri-information-line';
      case 'admin_message':
        return 'ri-admin-line';
      case 'nouveaute':
        return 'ri-megaphone-line';
      case 'badge_unlocked':
        return 'ri-award-line';
      case 'rank_change':
        return 'ri-bar-chart-line';
      default:
        return 'ri-notification-3-line';
    }
  };

  // Couleur selon le type de notification
  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'candidature_status':
        return 'text-blue-400';
      case 'ticket_reply':
        return 'text-green-400';
      case 'societe_status':
        return 'text-purple-400';
      case 'system':
        return 'text-gray-400';
      case 'admin_message':
        return 'text-[#DC143C]';
      case 'nouveaute':
        return 'text-orange-400';
      case 'badge_unlocked':
        return 'text-yellow-400';
      case 'rank_change':
        return 'text-cyan-400';
      default:
        return 'text-gray-400';
    }
  };

  // Formate la date relative
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bouton cloche */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
        aria-label="Notifications"
      >
        <i className="ri-notification-3-line text-xl"></i>
        
        {/* Badge compteur */}
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-[#DC143C] rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Panel des notifications - THÈME DARK TP13 */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-[#111111] rounded-lg shadow-xl border border-gray-800 z-50 max-h-[600px] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
            <h3 className="font-semibold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-[#DC143C] hover:text-[#B01030] font-medium whitespace-nowrap"
              >
                Tout marquer comme lu
              </button>
            )}
          </div>

          {/* Liste des notifications */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <i className="ri-notification-off-line text-4xl text-gray-700 mb-3"></i>
                <p className="text-gray-500 text-sm">Aucune notification</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {notifications.map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification.id, notification.link, notification.read)}
                    className={`w-full text-left px-4 py-3 hover:bg-[#1a1a1a] transition-colors ${
                      !notification.read ? 'bg-[#DC143C]/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icône */}
                      <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full ${
                        !notification.read ? 'bg-[#DC143C]/15 border border-[#DC143C]/30' : 'bg-gray-800'
                      }`}>
                        <i className={`${getNotificationIcon(notification.type)} text-lg ${
                          !notification.read ? 'text-[#DC143C]' : getNotificationColor(notification.type)
                        }`}></i>
                      </div>

                      {/* Contenu */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className={`text-sm font-medium ${
                            !notification.read ? 'text-white' : 'text-gray-300'
                          }`}>
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <span className="flex-shrink-0 w-2 h-2 bg-[#DC143C] rounded-full mt-1"></span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 line-clamp-2 mb-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-600">
                          {formatRelativeTime(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-800 bg-[#0a0a0a]">
              <button
                onClick={() => {
                  navigate('/dashboard/notifications');
                  setIsOpen(false);
                }}
                className="w-full text-center text-sm text-[#DC143C] hover:text-[#B01030] font-medium"
              >
                Voir toutes les notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}